export function formatBytes(bytes, decimals) {
  // https://gist.github.com/zentala/1e6f72438796d74531803cc3833c039c
  if (bytes == 0) return '0 Bytes'
  var k = 1024,
    dm = decimals || 2,
    sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

export function prob2sigma(x) {
  if (x === 1.0) return x * 100
  let z = 0
  const lim = 4.53988e-5
  const a = 0.2
  z = x < lim ? lim : x > 1.0 ? 1.0 : x
  let zz = 50.0 - (1.0 / a) * Math.log(1.0 / z - 1.0)
  if (zz < 0.000102575) zz = 0
  if (zz > 100.0) zz = 100.0
  return zz
}

export function sigma2prob(zz) {
  const a = 0.2
  const z = 1.0 / (Math.exp((50.0 - zz) * a) + 1)
  return z
}

export function rgbhash(string) {
  // see https://gist.github.com/0x263b/2bdd90886c2036a1ad5bcf06d6e6fb37
  let hash = 0
  if (string.length === 0) return hash
  for (var i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash)
    hash = hash & hash
  }
  const rgb = [0, 0, 0]
  for (var i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 255
    rgb[i] = value
  }

  return rgb
}

export function debounce(f, delay = 50) {
  let timeout = null
  function debounced(...args) {
    if (timeout !== null) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => f.apply(this, args), delay)
  }
  debounced.cancel = () => {
    if (timeout !== null) {
      clearTimeout(timeout)
      timeout = null
    }
  }
  return debounced
}

export function omitIf(data, condition) {
  const copy = {}
  for (let key in data) {
    if (!condition(data[key])) {
      copy[key] = data[key]
    }
  }
  return copy
}

export function replaceCSSCharsInCustomTagValue(str) {
  if (typeof str !== 'string') return str
  let newStr = str.replace(/[:;{}<>&]/g, match => {
    switch (match) {
      case ':':
        return '\\u003a'
      case ';':
        return '\\u003b'
      case '{':
        return '\\u007b'
      case '}':
        return '\\u007d'
      case '<':
        return '\\u003c'
      case '>':
        return '\\u003e'
      case '&':
        return '\\u0026'
      default:
        return match
    }
  })
  return newStr
}

const CUSTOM_TAG_ESCAPES = {
  '\\u003a': ':',
  '\\u003b': ';',
  '\\u007b': '{',
  '\\u007d': '}',
  '\\u003c': '<',
  '\\u003e': '>',
  '\\u0026': '&',
}

/**
 * Inverse of `replaceCSSCharsInCustomTagValue`, for showing a stored value to
 * the user. Never for storage: what goes back into `custom` stays escaped.
 */
export function decodeCustomTagValue(str) {
  if (typeof str !== 'string') return str
  return str.replace(
    /\\u00(3a|3b|7b|7d|3c|3e|26)/gi,
    match => CUSTOM_TAG_ESCAPES[match.toLowerCase()] ?? match
  )
}

/**
 * Returns a safe http(s) href for a value that is a whole URL, or null.
 * Bare `www.` URLs are normalized to https. Only http(s) is ever returned,
 * so the result is safe to use directly in an `href` (no `javascript:`/`data:`
 * — Vue does not sanitize `:href`, so the caller must rely on this).
 */
export function toSafeHref(value) {
  if (typeof value !== 'string') return null
  const v = value.trim()
  if (/^https?:\/\/\S+$/i.test(v)) return v
  if (/^www\.\S+$/i.test(v)) return `https://${v}`
  return null
}

/**
 * True when a plain string value (e.g. a tag attribute value) should be
 * rendered as a clickable link. Mirror of toSafeHref's accepted shapes.
 */
export function isUrl(value) {
  return toSafeHref(value) !== null
}

export function replaceUnallowedCharsInTagName(tagName) {
  if (typeof tagName === 'string' || tagName instanceof String) {
    // Define the regex pattern for valid tag names: must start with a letter or underscore,
    // followed by letters, numbers, underscores, or hyphens
    const validTagNamePattern = /^[_a-zA-Z][_a-zA-Z0-9-]*$/

    if (validTagNamePattern.test(tagName)) {
      // If the tag name already matches the pattern, return it as is
      return tagName
    } else {
      // For the first character: if not a letter or underscore, replace with underscore
      let cleanTagName = tagName.charAt(0).replace(/[^_a-zA-Z]/, '_')
      // For the rest of the string: replace any character that's not a letter, number, underscore, or hyphen
      if (tagName.length > 1) {
        cleanTagName += tagName.substring(1).replace(/[^_a-zA-Z0-9-]/g, '_')
      }

      return cleanTagName
    }
  } else {
    return tagName
  }
}
